import type React from 'react';

import { Separator } from '@change/ds-core-ui/Separator';
import { Text } from '@change/ds-core-ui/Text';
import { TrendingIcon } from '@change/ds-icons/TrendingIcon';

import { Translate, useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petition-page-spiking-mobile-experiment/petition-page-spiking-mobile-experiment.yml';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionShareCounts } from '#src/app/pages/petitionDetails/shared/hooks';

import { RecentSignersFeed } from '../../../MobileActionButton/components/RecentSignersFeed';

type Props = Readonly<{
	showSignerTicker: boolean;
}>;

const SpikingMomentumCardInner: React.FC<Props> = ({ showSignerTicker }) => {
	const { translate, translatePlural } = useI18n();
	const { totalShareCount, totalShareCountLocalized, isLoaded: isShareCountLoaded } = usePetitionShareCounts();
	const {
		data: {
			petition: { dailySignatureCount, dailySignatureCountLocalized, spotlightCategory, supporterCommentsConnection },
		},
	} = usePetitionDetailsPageData();

	if (dailySignatureCount === 0) return null;

	const countsLine = [
		// The share count is a separate client query: its segment joins the line once it has loaded.
		isShareCountLoaded &&
			!!totalShareCount &&
			translatePlural('corgi.petition_details.spiking_momentum_card.shares_count', totalShareCount, {
				countLocalized: totalShareCountLocalized ?? String(totalShareCount),
			}),
		supporterCommentsConnection.totalCount !== 0 &&
			translatePlural(
				'corgi.petition_details.spiking_momentum_card.comments_count',
				supporterCommentsConnection.totalCount,
				{
					countLocalized: supporterCommentsConnection.totalCountLocalized,
				},
			),
	]
		.filter(Boolean)
		.join(translate('corgi.petition_details.spiking_momentum_card.counts_separator'));
	const category = spotlightCategory ?? translate('corgi.petition_details.momentum_bar.spotlight.default_category');

	return (
		<div
			className="bg-gradient-background-weak-2 mb-4 flex flex-col gap-2 rounded-lg p-4"
			data-qa="spiking-momentum-card"
		>
			<Text as="p" variant="heading" size="large" className="text-typography-information">
				{dailySignatureCountLocalized}
			</Text>
			<Text as="p" variant="body" size="small">
				<Translate value="corgi.petition_details.spiking_momentum_card.caption" />
			</Text>
			{showSignerTicker && <RecentSignersFeed className="mb-0" fadeTo="transparent" />}
			<Separator orientation="horizontal" />
			<Text as="p" variant="body" size="medium" fontWeight="bold" className="flex items-center gap-2">
				<TrendingIcon size="small" className="text-icon-information" />
				{translate('corgi.petition_details.momentum_bar.spotlight.gaining_momentum_text', { category })}
			</Text>
			{countsLine && (
				<Text as="p" variant="body" size="small">
					{countsLine}
				</Text>
			)}
		</div>
	);
};

// EXPERIMENT: petition_page_spiking_mobile_experiment — remove with experiment cleanup
export const SpikingMomentumCard = withI18nPlaceholders(I18N_PLACEHOLDERS, SpikingMomentumCardInner);
